import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { InvoiceListItem, InvoiceStatus, SettlementMethod } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  grossInr,
  INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  SETTLEMENT_METHOD_LABELS,
  useInvoices,
  useSettleInvoice,
} from '../lib/billing.js';
import { formatDate, formatInr } from '../lib/format.js';
import { useCan } from '../lib/permissions.js';

/* ============================================================================
   /invoices — the invoice book.
   ----------------------------------------------------------------------------
   Readable by any admin: "has this client paid?" is a support question, and
   making staff ask the owner turns it into an interruption. Raising one is
   owner-only, which is why the button below is a link to a gated route rather
   than a form here.

   Nothing here marks a RAZORPAY payment paid — only a verified webhook does,
   because the browser's success callback is a claim by the party who benefits
   from it. `SettleControl` at the bottom is the other route to paid, for money
   that arrived as cash, UPI or a bank transfer, and it is owner-only. That is
   §14 question 6, now decided.
   ========================================================================== */

export function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const statusParam = params.get('status');
  const status = INVOICE_STATUSES.includes(statusParam as InvoiceStatus)
    ? (statusParam as InvoiceStatus)
    : undefined;

  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);

  const { data, isPending, error } = useInvoices({
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
  });

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const items = data?.items ?? [];
  const counts = data?.counts ?? {};
  /* Settling is owner-only, asked as the permission so the screen and the
     API read the same map. This hides the control; `requireRole('owner')` and
     `requirePermission` at the endpoint are what enforce it. */
  const maySettle = useCan()('invoices:settle');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Invoices</h1>
          <p className="muted">
            {data ? `${data.total} invoice` : 'Loading…'}
            {data && data.outstandingInr > 0
              ? ` · ${formatInr(data.outstandingInr)} outstanding`
              : ''}
          </p>
        </div>

        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            setParam('q', search.trim() || undefined);
          }}
        >
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="q" className="sr-only">
              Search by invoice number or business
            </label>
            <input
              id="q"
              type="search"
              placeholder="Number or business"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <button className="btn btn--ghost btn--small" type="submit">
            Dhoondhiye
          </button>
        </form>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={status ? 'btn btn--ghost btn--small' : 'btn btn--small'}
          onClick={() => setParam('status', undefined)}
        >
          All
        </button>
        {INVOICE_STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
            onClick={() => setParam('status', value)}
          >
            {INVOICE_STATUS_LABELS[value]} {counts[value] ?? 0}
          </button>
        ))}
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load invoices.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data && items.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            {status || q ? 'Nothing matches this filter.' : 'No invoices yet.'}
          </p>
          {!status && !q ? (
            <>
              <p className="muted" style={{ margin: '6px 0 0' }}>
                An invoice is created from inside a project.
              </p>
              <Link to="/projects" className="btn btn--small" style={{ marginTop: 12 }}>
                Open projects
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="stack">
        {items.map((invoice) => (
          <article className="card" key={invoice.id}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <strong>{invoice.number}</strong>
                <div className="lead-card__meta">
                  <Link to={`/clients/${invoice.clientId}`}>{invoice.business}</Link> ·{' '}
                  {INVOICE_TYPE_LABELS[invoice.type]}
                </div>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <span>{formatInr(invoice.amountInr)}</span>
                <span className={invoice.status === 'due' ? 'pill pill--warn' : 'pill'}>
                  {INVOICE_STATUS_LABELS[invoice.status]}
                </span>
              </div>
            </div>

            <div className="lead-card__meta" style={{ marginTop: 8 }}>
              {/* The figure the client actually pays, spelled out rather than
                  left for someone to compute in their head. */}
              {formatInr(grossInr(invoice))} with {invoice.gstPercent}% GST · raised{' '}
              {formatDate(invoice.createdAt)}
              {invoice.paidAt ? ` · paid ${formatDate(invoice.paidAt)}` : ''}
              {/* Who said so. Null settledMethod on a paid invoice means the
                  gateway confirmed it; a name means a person did. */}
              {invoice.settledMethod
                ? ` · ${SETTLEMENT_METHOD_LABELS[invoice.settledMethod]}${
                    invoice.settledRef ? ` (${invoice.settledRef})` : ''
                  }${invoice.settledByName ? ` · ${invoice.settledByName}` : ''}`
                : ''}
            </div>

            {invoice.status === 'due' && maySettle ? <SettleControl invoice={invoice} /> : null}
          </article>
        ))}
      </div>
    </>
  );
}

/* ============================================================================
   Recording a payment that never touched Razorpay.
   ----------------------------------------------------------------------------
   Owner-only on screen AND owner-only at the endpoint — this is the courtesy
   half, and it is not what keeps staff out.

   Two steps rather than one button per method. Marking money received cannot
   be undone from this screen, and a row of one-tap buttons next to a search
   field is a mis-tap away from settling the wrong invoice.
   ========================================================================== */

function SettleControl({ invoice }: { invoice: InvoiceListItem }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<SettlementMethod>('cash');
  const [reference, setReference] = useState('');
  const settle = useSettleInvoice();

  if (!open) {
    return (
      <div className="row" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn--small" onClick={() => setOpen(true)}>
          Mark as received
        </button>
      </div>
    );
  }

  return (
    <form
      className="row"
      style={{ marginTop: 12, alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}
      onSubmit={(event) => {
        event.preventDefault();
        settle.mutate(
          {
            id: invoice.id,
            method,
            ...(reference.trim() ? { reference: reference.trim() } : {}),
          },
          { onSuccess: () => setOpen(false) },
        );
      }}
    >
      <div className="field" style={{ minWidth: 110 }}>
        <label htmlFor={`method-${invoice.id}`}>How it arrived</label>
        <select
          id={`method-${invoice.id}`}
          value={method}
          onChange={(event) => setMethod(event.target.value as SettlementMethod)}
        >
          {(Object.keys(SETTLEMENT_METHOD_LABELS) as SettlementMethod[]).map((value) => (
            <option key={value} value={value}>
              {SETTLEMENT_METHOD_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="field" style={{ minWidth: 160 }}>
        <label htmlFor={`ref-${invoice.id}`}>Reference (optional)</label>
        <input
          id={`ref-${invoice.id}`}
          type="text"
          placeholder="UPI or cheque number"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
      </div>

      {/* The amount is deliberately not editable here. It is what the invoice
          says; a settle form that could change it would be a discount with no
          approval and no trail. */}
      <button className="btn btn--small" type="submit" disabled={settle.isPending}>
        {settle.isPending ? 'Saving…' : `${formatInr(grossInr(invoice))} received`}
      </button>
      <button
        type="button"
        className="btn btn--ghost btn--small"
        onClick={() => setOpen(false)}
        disabled={settle.isPending}
      >
        Never mind
      </button>

      {settle.isError ? (
        <p className="alert" role="alert" style={{ width: '100%', marginTop: 8 }}>
          {settle.error instanceof ApiError ? settle.error.message : 'Could not settle it.'}
        </p>
      ) : null}
    </form>
  );
}
